import React, {
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react'
import {
  Fields,
  Slot,
  SlotComponent,
  ComponentData,
  Data,
  renderContext,
  Render,
  usePuck,
} from '@puckeditor/core'
import ListicleStep from 'steps/listicle/ListicleStep3'
import { getPuckFileUrl } from 'breaktime-theme/src/puck/fields/FileUploadField'
import {
  BackgroundStyleProps,
  BorderStyleProps,
  TypographyProps,
  FontWeight,
  backgroundStyleField,
  borderStyleField,
  fontFamilyField,
  fontFamilyFallbackField,
  textTransformField,
  fontWeightOptions,
  applyBackgroundStyle,
  applyBorderStyle,
  applyTypography,
  splitBackgroundLayers,
} from 'breaktime-theme/src/puck/fields/StyleFields'
import { ColorField } from 'breaktime-theme/src/puck/fields/ColorField'
import {
  Spacing,
  defaultSpacing,
  SpacingGrid,
} from 'breaktime-theme/src/puck/fields/SpacingField'
import {
  buildItemTitleStyle,
  type ListicleItemProps,
} from '../ListicleItem/ListicleItem'
import { StartScreenTransition } from '../shared/StartScreen'

// Bundle the listicle styles into the puck iframe so the editor preview matches
// production (same approach as PollContainer importing PollStep3.scss).
import 'steps/listicle/listicle.scss'

export type ListicleContainerProps = {
  id?: string
  headline?: string
  headlineFontFamily?: TypographyProps['fontFamily']
  headlineFontFamilyFallback?: TypographyProps['fontFamilyFallback']
  headlineTextTransform?: TypographyProps['textTransform']
  headlineFontSize?: string
  headlineFontWeight?: FontWeight
  headlineColor?: string
  showNumbers?: boolean
  displayFormat: 'slideshow' | 'list'
  items: Slot
  startScreen: Slot
  background?: BackgroundStyleProps
  cardBackground?: BackgroundStyleProps
  cardBorder?: BorderStyleProps
  cardPadding?: Spacing
  cardMargin?: Spacing
}

const cssSpacingVal = (v?: string) =>
  !v || v === '0' ? undefined : /^\d+(\.\d+)?$/.test(v) ? `${v}px` : v

/** Inline padding/margin for the `.step-content` card — only sides the user set. */
export const buildCardSpacing = (
  padding?: Spacing,
  margin?: Spacing,
): React.CSSProperties => ({
  paddingTop: cssSpacingVal(padding?.top),
  paddingRight: cssSpacingVal(padding?.right),
  paddingBottom: cssSpacingVal(padding?.bottom),
  paddingLeft: cssSpacingVal(padding?.left),
  marginTop: cssSpacingVal(margin?.top),
  marginRight: cssSpacingVal(margin?.right),
  marginBottom: cssSpacingVal(margin?.bottom),
  marginLeft: cssSpacingVal(margin?.left),
})

const spacingFieldRender =
  (title: string) =>
  ({ value, onChange }: { value?: Spacing; onChange: (v: Spacing) => void }) =>
    (
      <SpacingGrid
        title={title}
        value={typeof value === 'object' && value ? value : defaultSpacing}
        onChange={onChange}
      />
    )

/** Just the headline typography fields, used to build the headline inline style. */
type HeadlineStyleProps = Pick<
  ListicleContainerProps,
  | 'headlineFontFamily'
  | 'headlineFontFamilyFallback'
  | 'headlineTextTransform'
  | 'headlineFontSize'
  | 'headlineFontWeight'
  | 'headlineColor'
>

/** Inline style for the list-format headline, from the headline typography fields. */
export const buildHeadlineStyle = (
  p: HeadlineStyleProps,
): React.CSSProperties => ({
  ...applyTypography({
    fontFamily: p?.headlineFontFamily,
    fontFamilyFallback: p?.headlineFontFamilyFallback,
    textTransform: p?.headlineTextTransform,
  }),
  ...(p?.headlineFontSize ? { fontSize: p.headlineFontSize } : {}),
  ...(p?.headlineFontWeight
    ? { fontWeight: Number(p.headlineFontWeight) || undefined }
    : {}),
  ...(p?.headlineColor ? { color: p.headlineColor } : {}),
})

type ListicleItemSlotItem = {
  type: 'ListicleItem'
  props: ListicleItemProps & {
    topRow?: ComponentData[]
    middleLeft?: ComponentData[]
    middleRight?: ComponentData[]
    bottomRow?: ComponentData[]
    voting?: ComponentData[]
  }
}

/** Subset of ListicleContainer props as stored in puck data (items is a raw array). */
type ListicleContainerData = Pick<
  ListicleContainerProps,
  'id' | 'headline' | 'showNumbers'
> & {
  displayFormat?: 'slideshow' | 'list'
  items?: ListicleItemSlotItem[]
}

/** Callbacks + context the break player passes through PuckStep metadata. */
type ListicleStepMetadata = {
  brk?: { id?: string }
  step?: { breakId?: string; [key: string]: unknown }
  onEngage?: () => void
  onAwardPoints?: () => void
  onChangeBackground?: (bg: {
    backgroundColor?: string
    backgroundImageSrc?: string
    mobileBackgroundImageSrc?: string
    backgroundSize?: string
    backgroundPosition?: string
    backgroundRepeat?: string
    backgroundOpacity?: number
  }) => void
  skipButton?: React.ReactNode
  nextStepButton?: React.ReactNode
}

type RenderCtx = React.ContextType<typeof renderContext>

export const LISTICLE_INITIAL_DATA: ListicleContainerProps = {
  headline: 'New Listicle',
  showNumbers: true,
  displayFormat: 'slideshow',
  items: [],
  startScreen: [],
  background: {},
  cardBackground: {},
  cardBorder: {},
}

export const LISTICLE_FIELDS: Fields<ListicleContainerProps, {}> = {
  showNumbers: {
    type: 'radio',
    label: 'Show Item Numbers',
    options: [
      { label: 'Yes', value: true },
      { label: 'No', value: false },
    ],
  },
  displayFormat: {
    type: 'radio',
    label: 'Display Format',
    options: [
      { label: 'Slideshow', value: 'slideshow' },
      { label: 'List', value: 'list' },
    ],
  },
  headline: { type: 'text', label: 'Headline' },
  headlineFontFamily: fontFamilyField,
  headlineFontFamilyFallback: fontFamilyFallbackField,
  headlineTextTransform: textTransformField,
  headlineFontSize: { type: 'text', label: 'Headline Font Size', placeholder: '24px' },
  headlineFontWeight: { type: 'select', label: 'Headline Font Weight', options: fontWeightOptions },
  headlineColor: {
    type: 'custom',
    label: 'Headline Color',
    render: (props: React.ComponentProps<typeof ColorField>) => (
      <ColorField {...props} />
    ),
  },
  items: { type: 'slot', allow: ['ListicleItem'] },
  startScreen: { type: 'slot', label: 'Start Screen', allow: ['PollStartScreen'] },
  background: { ...backgroundStyleField, label: 'Background' },
  cardBackground: { ...backgroundStyleField, label: 'Card Background' },
  cardBorder: { ...borderStyleField, label: 'Card Border' },
  cardPadding: {
    type: 'custom',
    label: 'Card Padding',
    render: spacingFieldRender('Padding'),
  },
  cardMargin: {
    type: 'custom',
    label: 'Card Margin',
    render: spacingFieldRender('Margin'),
  },
}

/**
 * Map puck item props onto the legacy `listicle` data shape consumed by
 * ListicleStep. Item content itself is delivered separately as rendered React
 * nodes (see `puckRows` injection in the renderer); here we only carry the
 * per-item metadata the runtime needs (id, subhead, voting config).
 */
const extractListicleDataFromPuckProps = (props: ListicleContainerData) => {
  const { id, headline, showNumbers, displayFormat = 'slideshow', items = [] } =
    props

  return {
    id,
    headline,
    showNumbers: showNumbers ?? true,
    displayFormat,
    items: items
      .filter(it => it.type === 'ListicleItem')
      .map((it, i) => ({
        id: it.props.id ?? `puck-li-${i}`,
        subhead: it.props.subhead,
        puckSubheadStyle: buildItemTitleStyle(it.props),
        // voting is enabled when a ListicleVoting block sits in the voting slot
        hasVoting: (it.props.voting ?? []).some(
          v => v.type === 'ListicleVoting',
        ),
      })),
  }
}

type RowNodes = {
  top: React.ReactNode
  middleLeft: React.ReactNode
  middleRight: React.ReactNode
  bottom: React.ReactNode
  voting: React.ReactNode
}

// container props as stored in puck data (items is the raw slot array)
type ListicleContainerStoredProps = Omit<ListicleContainerProps, 'items'> & {
  items?: ListicleItemSlotItem[]
}

// Extracted so hooks are always called unconditionally.
const PuckListicleContainerRenderer = ({ puckCtx }: { puckCtx: RenderCtx }) => {
  const container = puckCtx.data.content.find(
    c => c.type === 'ListicleContainer',
  )
  const containerProps = (container?.props ??
    {}) as ListicleContainerStoredProps
  const containerBackground: BackgroundStyleProps =
    containerProps.background ?? {}
  const containerCardBackground: BackgroundStyleProps =
    containerProps.cardBackground ?? {}
  const containerCardBorder: BorderStyleProps =
    containerProps.cardBorder ?? {}

  const {
    brk,
    step,
    onEngage,
    onAwardPoints,
    onChangeBackground,
    skipButton,
    nextStepButton,
  } = (puckCtx.metadata ?? {}) as ListicleStepMetadata

  const breakType: 'editorial' | 'sponsored' =
    (puckCtx.data.root?.props as { breakType?: 'editorial' | 'sponsored' })
      ?.breakType ?? 'editorial'

  // Memoised slot renderer — rebuilds only when config/breakType/metadata change.
  const slotContent = useCallback(
    (items: ComponentData[]): React.ReactNode => {
      if (!items.length) return null
      return (
        <Render
          config={puckCtx.config}
          data={
            { content: items, root: { props: { breakType } } } as Data
          }
          metadata={{ ...puckCtx.metadata, shareEventSlug: 'listicle' }}
        />
      )
    },
    [puckCtx.config, breakType, puckCtx.metadata],
  )

  // itemId -> rendered React nodes for each layout region (+ voting block)
  const rowNodeMap = useMemo(() => {
    const map = new Map<string, RowNodes>()
    ;(containerProps.items ?? []).forEach((it, i) => {
      const itemId = it.props?.id ?? `puck-li-${i}`
      map.set(itemId, {
        top: slotContent(it.props?.topRow ?? []),
        middleLeft: slotContent(it.props?.middleLeft ?? []),
        middleRight: slotContent(it.props?.middleRight ?? []),
        bottom: slotContent(it.props?.bottomRow ?? []),
        voting: slotContent(it.props?.voting ?? []),
      })
    })
    return map
  }, [slotContent, containerProps])

  const listicleData = useMemo(() => {
    const raw = extractListicleDataFromPuckProps(containerProps)
    return {
      ...raw,
      items: raw.items.map(item => ({
        ...item,
        puckRows: rowNodeMap.get(item.id) ?? null,
        puckVotingContent: rowNodeMap.get(item.id)?.voting ?? null,
      })),
    }
  }, [containerProps, rowNodeMap])

  // Full-bleed step background: push to the outer StepBackground (covers the
  // whole step panel, not just the listicle section) — same contract as poll.
  const bgStyle = applyBackgroundStyle(containerBackground)
  const hasBackground = Object.keys(bgStyle).length > 0
  const bgImageSrc = containerBackground?.backgroundImage
    ? getPuckFileUrl(containerBackground.backgroundImage)
    : ''
  const mobileBgImageSrc = containerBackground?.mobileBackgroundImage
    ? getPuckFileUrl(containerBackground.mobileBackgroundImage)
    : ''

  const onChangeBackgroundRef = useRef(onChangeBackground)
  useEffect(() => {
    onChangeBackgroundRef.current = onChangeBackground
  }, [onChangeBackground])

  useEffect(() => {
    const cb = onChangeBackgroundRef.current
    if (!cb || !hasBackground) return
    cb({
      backgroundColor: containerBackground?.backgroundColor,
      backgroundImageSrc: bgImageSrc || undefined,
      mobileBackgroundImageSrc: mobileBgImageSrc || undefined,
      backgroundSize: containerBackground?.backgroundSize,
      backgroundPosition: containerBackground?.backgroundPosition,
      backgroundRepeat: containerBackground?.backgroundRepeat,
      backgroundOpacity: containerBackground?.backgroundImageOpacity,
    })
  }, [
    hasBackground,
    containerBackground?.backgroundColor,
    bgImageSrc,
    mobileBgImageSrc,
    containerBackground?.backgroundSize,
    containerBackground?.backgroundPosition,
    containerBackground?.backgroundRepeat,
    containerBackground?.backgroundImageOpacity,
  ])

  // Card-level styling on the `.step-content.with-shadow` wrapper. Split the
  // background so image opacity < 1 renders as an overlay layer (same approach
  // poll uses) — a flat background can't apply opacity to a bg image.
  const cardLayers = splitBackgroundLayers(containerCardBackground)
  const puckCardStyle = {
    // puck isn't sponsored-type-aware, so default the card transparent instead
    // of the scss `var(--step-container-background-color)`. A chosen Card
    // Background color overrides this.
    backgroundColor: 'transparent',
    ...cardLayers.base,
    ...applyBorderStyle(containerCardBorder),
    ...buildCardSpacing(containerProps.cardPadding, containerProps.cardMargin),
  }
  const puckCardOverlayStyle = cardLayers.overlay

  const breakId = step?.breakId ?? brk?.id

  const listicleStep = (
    <ListicleStep
      breakId={breakId}
      step={{ ...(step ?? {}), listicle: listicleData }}
      onEngage={onEngage ?? (() => {})}
      onAwardPoints={onAwardPoints ?? (() => {})}
      skipButton={skipButton}
      nextStepButton={nextStepButton}
      puckCardStyle={puckCardStyle}
      puckCardOverlayStyle={puckCardOverlayStyle}
      puckHeadlineStyle={buildHeadlineStyle(containerProps)}
      isPuck
    />
  )

  // Optional start screen (shared block) — shows a start slide that transitions
  // to the listicle on click. No-op when the startScreen slot is empty.
  return (
    <StartScreenTransition
      startScreen={
        containerProps.startScreen as unknown as {
          type: string
          props?: Record<string, unknown>
        }[]
      }
    >
      {listicleStep}
    </StartScreenTransition>
  )
}

const PuckListicleEditorWrapper = ({
  children,
  startScreen: StartScreen,
  headline,
  headlineStyle,
  displayFormat,
  background,
  cardBackground,
  cardBorder,
  cardPadding,
  cardMargin,
}: PropsWithChildren<{
  startScreen?: SlotComponent
  headline?: string
  headlineStyle?: React.CSSProperties
  displayFormat: 'slideshow' | 'list'
  background?: BackgroundStyleProps
  cardBackground?: BackgroundStyleProps
  cardBorder?: BorderStyleProps
  cardPadding?: Spacing
  cardMargin?: Spacing
}>) => {
  const puck = usePuck()
  const isEditorial =
    puck?.appState?.data?.root?.props?.breakType === 'editorial'

  // Step-level background (sponsored only — hidden for editorial). In production
  // this is pushed to StepBackground via onChangeBackground; the editor canvas
  // has no StepBackground, so render it here for an accurate preview.
  const mainLayers = splitBackgroundLayers(background)
  const cardLayers = splitBackgroundLayers(cardBackground)
  const cardStyle = {
    // match production: transparent by default, overridden by a chosen color
    backgroundColor: 'transparent',
    ...cardLayers.base,
    ...applyBorderStyle(cardBorder),
    ...buildCardSpacing(cardPadding, cardMargin),
  }

  return (
    <main
      className={isEditorial ? 'editorial container' : 'sponsored'}
      style={{ ...mainLayers.base, display: 'flow-root' }}
    >
      {mainLayers.overlay && <div style={mainLayers.overlay} />}
      {StartScreen && (
        <div style={{ marginBottom: 12 }}>
          <span
            style={{
              display: 'block',
              fontSize: 10,
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.04em',
              color: 'rgba(0,0,0,0.4)',
              marginBottom: 4,
            }}
          >
            Start Screen (optional)
          </span>
          <StartScreen allow={['PollStartScreen']} minEmptyHeight={80} />
        </div>
      )}
      <section className={`listicle-step format-${displayFormat}`}>
        <div className="step-content with-shadow" style={cardStyle}>
          {cardLayers.overlay && <div style={cardLayers.overlay} />}
          {displayFormat === 'list' && headline && (
            <div className="step-intro">
              <h2
                className="step-content__title"
                style={headlineStyle}
                dangerouslySetInnerHTML={{ __html: headline }}
              />
            </div>
          )}
          {children}
        </div>
      </section>
    </main>
  )
}

type ListicleContainerRenderProps = Omit<
  ListicleContainerProps,
  'items' | 'startScreen'
> & {
  items: SlotComponent
  startScreen: SlotComponent
}

export const PuckListicleContainer = (props: ListicleContainerRenderProps) => {
  const {
    items: Items,
    startScreen,
    headline,
    displayFormat,
    background,
    cardBackground,
    cardBorder,
    cardPadding,
    cardMargin,
  } = props
  const puckCtx = useContext(renderContext)
  const isRenderMode = puckCtx.data.content.length > 0

  if (isRenderMode) {
    return <PuckListicleContainerRenderer puckCtx={puckCtx} />
  }

  return (
    <PuckListicleEditorWrapper
      startScreen={startScreen}
      headline={headline}
      headlineStyle={buildHeadlineStyle(props)}
      displayFormat={displayFormat}
      background={background}
      cardBackground={cardBackground}
      cardBorder={cardBorder}
      cardPadding={cardPadding}
      cardMargin={cardMargin}
    >
      <Items
        allow={['ListicleItem']}
        style={{ display: 'flex', flexDirection: 'column' }}
        minEmptyHeight={80}
      />
    </PuckListicleEditorWrapper>
  )
}
